import React, { useState, useEffect, useRef } from "react";
import "./gasChulaOrderForm.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faUser,
  faCalendarAlt,
  faTools,
  faFire,
  faWrench,
  faCommentAlt,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { URL } from "../../../config";

// const API_BASE = "http://localhost:4001";

const GasChulaOrderForm = ({
  user,
  formData,
  onClose,
  onSubmit,
  previousServitorName,
  serviceId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [divisions, setDivisions] = useState([]); // [{Id,name,code}]
  const [areas, setAreas] = useState([]);         // [{Id,name}]
  const [selectedDivisionId, setSelectedDivisionId] = useState(null);
  const [selectedAreaId, setSelectedAreaId] = useState(null);

  const [orderData, setOrderData] = useState({
    // city: user?.city || "",
    address: user?.Address || "",
    mobile: user?.mobile || "",
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [showBlockedPopup, setShowBlockedPopup] = useState(false);

  const [errors, setErrors] = useState({ city: "", area: "", address: "" });

  // For coupons
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const divisionRef = useRef(null);
  const areaRef = useRef(null);
  const addressRef = useRef(null);

  // const penalty = user && user.penalty ? Number(user.penalty) : 0;
  const basePrice = Number(formData.price) || 0;
  // const finalPrice = user.penaltyReserved ? basePrice : basePrice + penalty;
  const [discountValue, setDiscountValue] = useState(0);
  
  // const finalPrice = (() => {
  //   const total = user.penaltyReserved ? basePrice : basePrice + penalty;
  //   return Math.max(0, total - discountValue);
  // })();

  const finalPrice = Math.max(0, basePrice - discountValue);

  useEffect(() => {
    if (user) {
      if (user.status === "Blocked") {
        setShowBlockedPopup(true);
        setShowForm(false);
      } else {
        setOrderData({
          city: user.city || "",
          address: user.Address || "",
          mobile: user.mobile || "",
        });
      }
    }
  }, [user]);

  // Disable background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setOrderData((prevData) => ({
  //     ...prevData,
  //     [name]: value,
  //   }));
  // };


  // Load divisions and preselect by user.city (by name)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${URL}/locations/divisions`, {
          params: { countryCode: "BD" },
        });
        setDivisions(data || []);
        if (user?.city) {
          const match = (data || []).find(
            (d) => d.name.toLowerCase() === String(user.city).toLowerCase()
          );
          if (match) setSelectedDivisionId(match.Id);
        }
      } catch (e) {
        console.error("Failed to load divisions:", e);
        setDivisions([]);
      }
    })();
  }, [user?.city]);

  // Load areas and preselect by user.area (by name)
  useEffect(() => {
    (async () => {
      if (!selectedDivisionId) {
        setAreas([]);
        setSelectedAreaId(null);
        return;
      }
      try {
        const { data } = await axios.get(`${URL}/locations/areas`, {
          params: { divisionId: selectedDivisionId },
        });
        setAreas(data || []);
        if (user?.area) {
          const match = (data || []).find(
            (a) => a.name.toLowerCase() === String(user.area).toLowerCase()
          );
          setSelectedAreaId(match ? match.Id : null);
        } else {
          setSelectedAreaId(null);
        }
      } catch (e) {
        console.error("Failed to load areas:", e);
        setAreas([]);
        setSelectedAreaId(null);
      }
    })();
  }, [selectedDivisionId, user?.area]);

  const clearError = (key) => setErrors((prev) => ({ ...prev, [key]: "" }));

  const handleDivisionChange = (e) => {
    const id = e.target.value ? Number(e.target.value) : null;
    setSelectedDivisionId(id);
    clearError("city");
  };

  const handleAreaChange = (e) => {
    const id = e.target.value ? Number(e.target.value) : null;
    setSelectedAreaId(id);
    clearError("area");
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setOrderData((prev) => ({ ...prev, address: value }));
    if (value?.trim()) clearError("address");
  };

  const validate = () => {
    const next = { city: "", area: "", address: "" };
    if (!selectedDivisionId) next.city = t("Please select your city");
    if (!selectedAreaId) next.area = t("Please select your area");
    if (!orderData.address?.trim()) next.address = t("Please enter your address");
    setErrors(next);
    return !next.city && !next.area && !next.address;
  };


  const formatDate = (dateStr) => {
    const dateObj = new Date(dateStr);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString("default", { month: "short" });
    const year = dateObj.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!validate()) {
      if (!selectedDivisionId && divisionRef.current) {
        divisionRef.current.focus();
        divisionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (!selectedAreaId && areaRef.current) {
        areaRef.current.focus();
        areaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (!orderData.address?.trim() && addressRef.current) {
        addressRef.current.focus();
        addressRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    const selectedDivision = divisions.find((d) => d.Id === selectedDivisionId);
    const selectedArea = areas.find((a) => a.Id === selectedAreaId);

    const finalOrderData = {
      userId: user.Id,

      // pass NAMES, server stores City/Area strings
      usercity: selectedDivision?.name || "",
      userarea: selectedArea?.name || "",
      useraddress: orderData.address,

      chulaCylinderType: formData.chulaCylinderType,
      NewchulaSetup: formData.NewchulaSetup,
      chulaSetup: formData.chulaSetup,
      burnerSetup: formData.burnerSetup,
      burnerCleaning: formData.burnerCleaning,
      nozzleReplacement: formData.nozzleReplacement,
      checkIssues: formData.checkIssues,
      remarks: formData.remarks,

      // price: formData.price, 
      price: finalPrice,

      date: formData.date,
      time: formData.time,
      status: "pending",

      // Rebook parity with other forms
      useSameServitor: formData.useSameServitor,
      previousServitorId: formData.servitor_id,
      servitor_id: formData.useSameServitor ? formData.servitor_id : 0,

      serviceId: serviceId || 4,
    };

    if (appliedCoupon) {
      finalOrderData.appliedCoupon = appliedCoupon;
      finalOrderData.discountValue = discountValue;
    }

    try {
      setIsSubmitting(true);

      await axios.post(`${URL}/gaschula`, finalOrderData);
      setShowForm(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error placing order:", error);
      alert(error?.response?.data?.message || "Failed to place order");
    } finally {
      setIsSubmitting(false);
    }
  };


  // === Coupon handling ===
    const handleApplyCoupon = async () => {
      if (!couponCode.trim()) {
        setCouponError("Please enter a coupon code.");
        return;
      }
      setIsApplyingCoupon(true);
      setCouponError("");
      try {
        const res = await axios.post(`${URL}/usercoupon/apply`, {
          userId: user.Id,
          code: couponCode.trim(),
        });
        const { discountValue, code } = res.data;
        setAppliedCoupon(code);
        setDiscountValue(discountValue);
        setCouponError("");
      } catch (err) {
        setAppliedCoupon(null);
        setDiscountValue(0);
        setCouponError(err?.response?.data?.message || "Invalid or expired coupon.");
      } finally {
        setIsApplyingCoupon(false);
      }
    };
  
    const handleRemoveCoupon = async () => {
      // try {
      //   await axios.post(`${URL}/usercoupon/remove`, {
      //     userId: user.Id,
      //     code: appliedCoupon,
      //   });
      // } catch (err) {
      //   console.warn("Coupon removal failed (non-blocking):", err);
      // }
      setAppliedCoupon(null);
      setDiscountValue(0);
      setCouponCode("");
      setCouponError("");
    };
  


  const closePopup = () => {
    setShowSuccessPopup(false);
    onClose();
    onSubmit();
    navigate("/profile/orderhistory");
  };

  const closeBlockedPopup = () => {
    setShowBlockedPopup(false);
    onClose();
  };

  return (
    <>
      {showForm && (
        <div className="gaschulaorder-modal-backdrop">
          <div className="gaschulaorder-modal-content">
            <div className="gaschulaorder-modal-header">
              <h2 className="gaschulaorder-modal-title">
                {t("gasChula.order_form.modal.title")}
              </h2>
              <button
                className="gaschulaorder-modal-close"
                onClick={onClose}
                aria-label={t("gasChula.order_form.modal.close_button")}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="gaschulaorder-modal-body">
              <div className="gaschulaorder-two-column-grid">
                <div className="gaschulaorder-modal-card">
                  <div className="gaschulaorder-card-body">
                    <h3 className="gaschulaorder-card-title">
                      <FontAwesomeIcon icon={faUser} />
                      {t("gasChula.order_form.customer_information.title")}
                    </h3>
                    <div className="gaschulaorder-form-grid">
                      <div className="gaschulaorder-form-group">
                        <label className="gaschulaorder-form-label">
                          {t("gasChula.order_form.customer_information.labels.name")}
                        </label>
                        <input
                          type="text"
                          className="gaschulaorder-form-control"
                          value={`${user.FirstName} ${user.LastName}`}
                          readOnly
                        />
                      </div>
                      <div className="gaschulaorder-form-group">
                        <label className="gaschulaorder-form-label">
                          {t("gasChula.order_form.customer_information.labels.mobile")}
                        </label>
                        <input
                          type="text"
                          className="gaschulaorder-form-control"
                          value={`${orderData.mobile}`}
                          readOnly
                        />
                      </div>
                      <div className="gaschulaorder-form-group">
                        <label className="gaschulaorder-form-label">
                          {t("gasChula.order_form.customer_information.labels.city")}
                        </label>
                        <select
                          className={`gaschulaorder-form-control ${errors.city ? "hc-error" : ""}`}
                          value={selectedDivisionId || ""}
                          onChange={handleDivisionChange}
                          onBlur={validate}
                          aria-invalid={Boolean(errors.city)}
                          aria-describedby={errors.city ? "city-error" : undefined}
                          ref={divisionRef}
                        >
                          <option value="">
                            {t("plumberorderform.order_form.customer_information.labels.city_placeholder")}
                          </option>
                          {divisions.map((d) => (
                            <option key={d.Id} value={d.Id}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                        {errors.city && (
                          <small id="city-error" className="hc-error-text">
                            {errors.city}
                          </small>
                        )}
                      </div>

                      {/* Area */}
                      <div className="gaschulaorder-form-group">
                        <label className="gaschulaorder-form-label">
                          {t("gasChula.order_form.customer_information.labels.area", "Area")}
                        </label>
                        <select
                          className={`gaschulaorder-form-control ${errors.area ? "hc-error" : ""}`}
                          value={selectedAreaId || ""}
                          onChange={handleAreaChange}
                          onBlur={validate}
                          disabled={!selectedDivisionId}
                          aria-invalid={Boolean(errors.area)}
                          aria-describedby={errors.area ? "area-error" : undefined}
                          ref={areaRef}
                        >
                          <option value="">
                            {!selectedDivisionId ? t("Select City first") : t("Select Area")}
                          </option>
                          {areas.map((a) => (
                            <option key={a.Id} value={a.Id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                        {errors.area && (
                          <small id="area-error" className="hc-error-text">
                            {errors.area}
                          </small>
                        )}
                      </div>

                      <div className="gaschulaorder-form-group">
                        <label className="gaschulaorder-form-label">
                          {t("gasChula.order_form.customer_information.labels.address")}
                        </label>
                        <textarea
                          className={`gaschulaorder-form-control ${errors.address ? "hc-error" : ""}`}
                          rows="3"
                          value={orderData.address}
                          onChange={handleAddressChange}
                          onBlur={validate}
                          aria-invalid={Boolean(errors.address)}
                          aria-describedby={errors.address ? "address-error" : undefined}
                          ref={addressRef}
                        />
                        {errors.address && (
                          <small id="address-error" className="hc-error-text">
                            {errors.address}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="gaschulaorder-modal-card">
                  <div className="gaschulaorder-card-body">
                    <h3 className="gaschulaorder-card-title">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      {t("gasChula.order_form.booking_details.title")}
                    </h3>
                    <div className="gaschulaorder-form-grid">
                      <div className="gaschulaorder-form-group">
                        <label className="gaschulaorder-form-label">
                          {t("gasChula.order_form.booking_details.labels.date")}
                        </label>
                        <input
                          type="text"
                          className="gaschulaorder-form-control"
                          value={formatDate(formData.date)}
                          readOnly
                        />
                      </div>
                      <div className="gaschulaorder-form-group">
                        <label className="gaschulaorder-form-label">
                          {t("gasChula.order_form.booking_details.labels.time")}
                        </label>
                        <input
                          type="text"
                          className="gaschulaorder-form-control"
                          value={formData.time}
                          readOnly
                        />
                      </div>
                      {/* {!user.penaltyReserved && penalty > 0 ? (
                        <>
                          <div className="gaschulaorder-form-group">
                            <label className="gaschulaorder-form-label">
                              {t("gasChula.order_form.booking_details.labels.base_price")}
                            </label>
                            <input
                              type="text"
                              className="gaschulaorder-form-control"
                              value={`${basePrice} tk`}
                              readOnly
                            />
                          </div>
                          <div className="gaschulaorder-form-group">
                            <label className="gaschulaorder-form-label">
                              {t("gasChula.order_form.booking_details.labels.penalty")}
                            </label>
                            <input
                              type="text"
                              className="gaschulaorder-form-control gaschulaorder-penalty-field"
                              value={`${penalty} tk`}
                              readOnly
                            />
                          </div>
                          <div className="gaschulaorder-form-group">
                            <label className="gaschulaorder-form-label">
                              {t("gasChula.order_form.booking_details.labels.total_price")}
                            </label>
                            <input
                              type="text"
                              className="gaschulaorder-form-control gaschulaorder-total-price"
                              value={`${finalPrice} tk`}
                              readOnly
                            />
                          </div>
                        </>
                      ) : (
                        // New design for dicount
                        <div className="electricianorder-form-group">
                        <label className="electricianorder-form-label">
                          {t("electricianorderform.order_form.booking_details.labels.total_price")}
                        </label>

                        {appliedCoupon ? (
                          <div className="electricianorder-price-discounted">
                            <div className="electricianorder-original-price">
                              {user.penaltyReserved
                                ? `${basePrice} tk`
                                : `${basePrice + penalty} tk`}
                            </div>

                            <div className="electricianorder-final-price">
                              <span className="discounted-price">{finalPrice} tk</span>
                              <small className="discount-label">({discountValue} tk off)</small>
                            </div>
                          </div>
                        ) : (
                          <input
                            type="text"
                            className="electricianorder-form-control electricianorder-total-price"
                            value={`${finalPrice} tk`}
                            readOnly
                          />
                        )}
                      </div>

                      )} */}

                      <div className="electricianorder-form-group">
                        <label className="electricianorder-form-label">
                          {t("electricianorderform.order_form.booking_details.labels.total_price")}
                        </label>

                        {appliedCoupon ? (
                          <div className="electricianorder-price-discounted">
                            <div className="electricianorder-original-price">
                              {basePrice} tk
                            </div>

                            <div className="electricianorder-final-price">
                              <span className="discounted-price">{finalPrice} tk</span>
                              <small className="discount-label">({discountValue} tk off)</small>
                            </div>
                          </div>
                        ) : (
                          <input
                            type="text"
                            className="electricianorder-form-control electricianorder-total-price"
                            value={`${finalPrice} tk`}
                            readOnly
                          />
                        )}
                      </div>

                      {/* Coupon Code */}
                      <div className="electricianorder-form-group">
                        <label className="electricianorder-form-label">
                          {t("electricianorderform.order_form.booking_details.labels.coupon", "Coupon Code")}
                        </label>

                        {appliedCoupon ? (
                          <div className="electricianorder-coupon-applied">
                            {/* <p>
                              ✅ Applied: <strong>{appliedCoupon}</strong>
                            </p> */}
                             <p>
                                ✅ {t("electricianorderform.coupon.applied", "Applied")}:{" "}
                                <strong>{appliedCoupon}</strong>
                              </p>
                            <button
                              type="button"
                              // className="electricianorder-btn electricianorder-btn-outline"
                              className="electricianorder-btn-removecoupon"
                              onClick={handleRemoveCoupon}
                            >
                              {/* Remove */}
                              {t("electricianorderform.coupon.remove", "Remove")}
                            </button>
                          </div>
                        ) : (
                          <div className="electricianorder-coupon-input-row">
                            <input
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              placeholder={t("electricianorderform.order_form.booking_details.labels.coupon_placeholder", "Enter coupon")}
                              className="electricianorder-form-control"
                              disabled={isApplyingCoupon}
                            />
                            <button
                              type="button"
                              // className="electricianorder-btn electricianorder-btn-primary"
                              className="electricianorder-btn-applycoupon"
                              onClick={handleApplyCoupon}
                              disabled={isApplyingCoupon}
                            >
                              {/* {isApplyingCoupon ? "Applying..." : "Apply"} */}
                              {isApplyingCoupon
                                ? t("electricianorderform.coupon.applying", "Applying...")
                                : t("electricianorderform.coupon.apply", "Apply")}
                            </button>
                          </div>
                        )}

                        {/* {couponError && <div className="field-error">{couponError}</div>} */}

                        {couponError && (
                          <div className="field-error">
                            {t("electricianorderform.coupon.invalid", couponError)}
                          </div>
                        )}

                      </div>

                    </div>
                  </div>
                </div>
              </div>

              <div className="gaschulaorder-modal-card">
                <div className="gaschulaorder-card-body">
                  <h3 className="gaschulaorder-card-title">
                    {t("gasChula.order_form.service_details.title")}
                  </h3>
                  <div className="gaschulaorder-services-section">
                    <div className="gaschulaorder-service-category">
                      <h4 className="gaschulaorder-category-header">
                        <FontAwesomeIcon icon={faFire} />
                        {t("gasChula.order_form.service_details.categories.cylinder_type.title")}
                      </h4>
                      <div className="gaschulaorder-services-grid">
                        <div className="gaschulaorder-service-item">
                          <label className="gaschulaorder-form-label">
                            {t("gasChula.order_form.service_details.categories.cylinder_type.labels.cylinder_type")}
                          </label>
                          <div
                            className={`gaschulaorder-service-value ${
                              formData.chulaCylinderType ? "has-value" : ""
                            }`}
                          >
                            {formData.chulaCylinderType}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="gaschulaorder-service-category">
                      <h4 className="gaschulaorder-category-header">
                        <FontAwesomeIcon icon={faTools} />
                        {t("gasChula.order_form.service_details.categories.setup_services.title")}
                      </h4>
                      <div className="gaschulaorder-services-grid">
                        <div className="gaschulaorder-service-item">
                          <label className="gaschulaorder-form-label">
                            {t("gasChula.order_form.service_details.categories.setup_services.labels.new_chula_setup")}
                          </label>
                          <div
                            className={`gaschulaorder-service-value ${
                              formData.NewchulaSetup > 0 ? "has-value" : ""
                            }`}
                          >
                            {formData.NewchulaSetup}{" "}
                            <span className="gaschulaorder-service-unit">
                              {t(
                                formData.NewchulaSetup === 1
                                  ? "gasChula.order_form.service_details.unit"
                                  : "gasChula.order_form.service_details.units"
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="gaschulaorder-service-item">
                          <label className="gaschulaorder-form-label">
                            {t("gasChula.order_form.service_details.categories.setup_services.labels.chula_setup")}
                          </label>
                          <div
                            className={`gaschulaorder-service-value ${
                              formData.chulaSetup > 0 ? "has-value" : ""
                            }`}
                          >
                            {formData.chulaSetup}{" "}
                            <span className="gaschulaorder-service-unit">
                              {t(
                                formData.chulaSetup === 1
                                  ? "gasChula.order_form.service_details.unit"
                                  : "gasChula.order_form.service_details.units"
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="gaschulaorder-service-item">
                          <label className="gaschulaorder-form-label">
                            {t("gasChula.order_form.service_details.categories.setup_services.labels.burner_setup")}
                          </label>
                          <div
                            className={`gaschulaorder-service-value ${
                              formData.burnerSetup > 0 ? "has-value" : ""
                            }`}
                          >
                            {formData.burnerSetup}{" "}
                            <span className="gaschulaorder-service-unit">
                              {t(
                                formData.burnerSetup === 1
                                  ? "gasChula.order_form.service_details.unit"
                                  : "gasChula.order_form.service_details.units"
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="gaschulaorder-service-category">
                      <h4 className="gaschulaorder-category-header">
                        <FontAwesomeIcon icon={faWrench} />
                        {t("gasChula.order_form.service_details.categories.maintenance_services.title")}
                      </h4>
                      <div className="gaschulaorder-services-grid">
                        <div className="gaschulaorder-service-item">
                          <label className="gaschulaorder-form-label">
                            {t("gasChula.order_form.service_details.categories.maintenance_services.labels.burner_cleaning")}
                          </label>
                          <div
                            className={`gaschulaorder-service-value ${
                              formData.burnerCleaning > 0 ? "has-value" : ""
                            }`}
                          >
                            {formData.burnerCleaning}{" "}
                            <span className="gaschulaorder-service-unit">
                              {t(
                                formData.burnerCleaning === 1
                                  ? "gasChula.order_form.service_details.unit"
                                  : "gasChula.order_form.service_details.units"
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="gaschulaorder-service-item">
                          <label className="gaschulaorder-form-label">
                            {t("gasChula.order_form.service_details.categories.maintenance_services.labels.nozzle_replacement")}
                          </label>
                          <div
                            className={`gaschulaorder-service-value ${
                              formData.nozzleReplacement > 0 ? "has-value" : ""
                            }`}
                          >
                            {formData.nozzleReplacement}{" "}
                            <span className="gaschulaorder-service-unit">
                              {t(
                                formData.nozzleReplacement === 1
                                  ? "gasChula.order_form.service_details.unit"
                                  : "gasChula.order_form.service_details.units"
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="gaschulaorder-service-item">
                          <label className="gaschulaorder-form-label">
                            {t("gasChula.order_form.service_details.categories.maintenance_services.labels.check_issues")}
                          </label>
                          <div
                            className={`gaschulaorder-service-value ${
                              formData.checkIssues ? "boolean-true" : ""
                            }`}
                          >
                            {t(
                              `gasChula.order_form.service_details.boolean.${
                                formData.checkIssues ? "yes" : "no"
                              }`
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {formData.useSameServitor && previousServitorName && (
                      <div className="gaschulaorder-service-category">
                        <div className="gaschulaorder-service-item gaschulaorder-rebook-item">
                          <label className="gaschulaorder-form-label gaschulaorder-label-content">
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="gaschulaorder-rebook-icon"
                            />
                            {t("gasChula.order_form.rebook.label")}
                          </label>
                          <div className="gaschulaorder-service-value gaschulaorder-rebook-value">
                            <strong>{previousServitorName}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {formData.remarks && (
                      <div className="gaschulaorder-service-category">
                        <div className="gaschulaorder-service-item gaschulaorder-remarks-item">
                          <label className="gaschulaorder-form-label gaschulaorder-label-content">
                            <FontAwesomeIcon
                              icon={faCommentAlt}
                              className="gaschulaorder-remarks-icon"
                            />
                            {t("gasChula.order_form.service_details.categories.remarks.labels.remarks")}
                          </label>
                          <div className="gaschulaorder-service-value has-value gaschulaorder-remarks-value">
                            {formData.remarks}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="gaschulaorder-order-actions">
              <button
                className="gaschulaorder-btn gaschulaorder-btn-outline"
                onClick={onClose}
              >
                {t("gasChula.order_form.actions.cancel")}
              </button>
              <button
                className="gaschulaorder-btn gaschulaorder-btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faTools} />
                {/* {t("gasChula.order_form.actions.place_order")} */}
                {isSubmitting
                  ? t("gasChula.order_form.actions.placing", "Placing...")
                  : t("gasChula.order_form.actions.place_order")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessPopup && (
        <div className="gaschulaorder-success-popup">
          <div className="gaschulaorder-success-popup-content">
            <h2>{t("gasChula.order_form.success_popup.title")}</h2>
            <p>{t("gasChula.order_form.success_popup.message1")}</p>
            <p>{t("gasChula.order_form.success_popup.message2")}</p>
            <button
              className="gaschulaorder-btn gaschulaorder-btn-primary"
              onClick={closePopup}
            >
              {t("gasChula.order_form.success_popup.button")}
            </button>
          </div>
        </div>
      )}

      {showBlockedPopup && (
        <div className="gaschulaorder-success-popup">
          <div className="gaschulaorder-success-popup-content">
            <h2>{t("gasChula.order_form.blocked_popup.title")}</h2>
            <p>{t("gasChula.order_form.blocked_popup.message1")}</p>
            <p>{t("gasChula.order_form.blocked_popup.message2")}</p>
            <button
              className="gaschulaorder-btn gaschulaorder-btn-primary"
              onClick={closeBlockedPopup}
            >
              {t("gasChula.order_form.blocked_popup.button")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GasChulaOrderForm;